export function createLazyInstance<R extends object>(createFunc: () => R): R;
export function createLazyInstance<R extends object, Args extends unknown[]>(
  createFunc: (...a: Args) => R,
  args: Args,
): R;
export function createLazyInstance<R extends object, Args extends unknown[]>(
  createFunc: (...a: Args) => R,
  args?: Args,
) {
  let instance: R | null = null;

  const getInstance = () => {
    instance = instance ?? createFunc(...(args ?? ([] as unknown[] as Args)));
    return instance as Record<string | symbol, unknown>;
  };

  return new Proxy<R>({} as R, {
    get(_target, property: string | symbol) {
      const item = getInstance()[property];
      if (typeof item === 'function') {
        return function makeCall(...callArgs: unknown[]) {
          return (item as (...a: unknown[]) => unknown).call(instance, ...callArgs);
        };
      }
      return item;
    },

    set(_target, property: string | symbol, value: unknown) {
      getInstance()[property] = value;
      return true;
    },
  });
}

/**
 * Creates a lazy-loaded module wrapper.
 *
 * Use this to wrap third-party libraries that:
 * 1. Have large bundle sizes (defers loading until needed)
 * 2. Have browser-only dependencies like CSS (isolates them from Node tests)
 * 3. Should be isolated from the rest of the codebase
 *
 * @example
 * // Create a lazy loader for a heavy library
 * export const emojiMart = createLazyModule(async () => {
 *   await import('emoji-mart-vue-fast/css/emoji-mart.css');
 *   const { Picker, EmojiIndex } = await import('emoji-mart-vue-fast/src');
 *   const data = (await import('emoji-mart-vue-fast/data/apple.json')).default;
 *   return { Picker, EmojiIndex, data };
 * });
 *
 * // Use it (loads on first call, cached thereafter)
 * const { Picker, data } = await emojiMart.load();
 */
export function createLazyModule<T>(loader: () => Promise<T>): {
  load: () => Promise<T>;
  isLoaded: () => boolean;
  reset: () => void;
} {
  let cached: T | null = null;
  let loading: Promise<T> | null = null;

  return {
    async load() {
      if (cached) return cached;
      if (loading) return loading;

      loading = loader().then((result) => {
        cached = result;
        loading = null;
        return result;
      });

      return loading;
    },

    isLoaded() {
      return cached !== null;
    },

    reset() {
      cached = null;
      loading = null;
    },
  };
}
